import { emailEscape } from './brandedEmail';
import { HELP_VIDEO_BASE } from './help';

/**
 * «Alexia tiene nueva cara» (lanzamiento del rediseño, 18 oct 2026): un
 * pop-up en la web y un correo a todas las personas con cuenta. Los textos
 * son los de Alex, tal cual (popup.html y correo.html).
 */

/** Clave del aviso en `Membership.avisosVistos` y de la campaña de correo. */
export const NOVEDADES_ID = 'novedades-2026-10';

/**
 * Hora del lanzamiento (18 oct 2026, 21:30 en Madrid). Quien tenía cuenta
 * ANTES conoce la versión vieja: a esas personas se les enseña el pop-up y se
 * les manda el correo. Quien se da de alta después ya ve la versión nueva
 * desde el principio y no recibe ninguno de los dos.
 */
export const NOVEDADES_CORTE = '2026-10-18T19:30:00Z';

/** Página pública con el vídeo (el correo no puede reproducirlo). */
export const RUTA_NOVEDADES = '/novedades';

/** El vídeo va al AyudaBucket de producción, junto a los de la Ayuda (`npm run ayuda:videos`). */
export const NOVEDADES_VIDEO_URL = `${HELP_VIDEO_BASE}/novedades/alexia-nueva-cara-2026-10.mp4`;
/** Miniatura y póster los sirve la propia app (packages/web/public/novedades/). */
export const NOVEDADES_MINIATURA = '/novedades/miniatura-video.jpg';
export const NOVEDADES_POSTER = '/novedades/poster.jpg';
export const NOVEDADES_DURACION = '1:11';

/**
 * Las fichas de la app en las tiendas (iOS: ascAppId de packages/mobile/eas.json;
 * Android: el package de app.config.ts). Aquí para que las usen la web y el correo.
 */
export const APP_STORE_URL = 'https://apps.apple.com/app/id6815699830';
export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.alexiarest.mobile';

/** ¿Tenía cuenta antes del lanzamiento? Sin fecha = cuenta histórica (anterior a las membresías con fecha). */
export function teniaCuentaAntesDeNovedades(createdAt: string | undefined): boolean {
  if (!createdAt) return true;
  const t = Date.parse(createdAt);
  return !Number.isFinite(t) || t < Date.parse(NOVEDADES_CORTE);
}

/** ¿Le toca el pop-up? Cuenta anterior al corte y sin haberlo cerrado. */
export function tocaPopupNovedades(persona: { createdAt?: string; avisosVistos?: string[] }): boolean {
  return teniaCuentaAntesDeNovedades(persona.createdAt) && !(persona.avisosVistos ?? []).includes(NOVEDADES_ID);
}

export const ASUNTO_NOVEDADES = 'Alexia tiene nueva cara';
export const PREHEADER_NOVEDADES = 'Tus datos están igual. Te contamos dónde quedó cada cosa.';

export interface EnlacesCorreoNovedades {
  /** https://app.alexiarest.com (o la de ese entorno). */
  appUrl: string;
  /** El número de soporte: https://wa.me/… (SupportButton). */
  whatsappUrl: string;
  /** Donde viven el logo y la miniatura (URLs absolutas). Por defecto, producción. */
  imagenesBase?: string;
  /** Página del vídeo. Por defecto, https://app.alexiarest.com/novedades. */
  videoUrl?: string;
}

const PROD = 'https://app.alexiarest.com';

/** «Hola, Carlos:» o, sin nombre, «Hola:». */
export function saludoNovedades(nombre: string | undefined): string {
  const n = (nombre ?? '').trim().replace(/\s+/g, ' ');
  return n ? `Hola, ${n}:` : 'Hola:';
}

/**
 * El correo de Alex (correo.html) con los huecos rellenos. Correo de CUENTA
 * (cambio del producto, sin ofertas): no hay baja de verdad, así que el
 * «Darme de baja» del pie pasa a «Gestionar correos», que lleva a la
 * Configuración de la app.
 */
export function correoNovedades(nombre: string | undefined, enlaces: EnlacesCorreoNovedades): { asunto: string; preheader: string; html: string; texto: string } {
  const app = enlaces.appUrl.replace(/\/$/, '');
  const img = (enlaces.imagenesBase ?? PROD).replace(/\/$/, '');
  const video = enlaces.videoUrl ?? `${PROD}${RUTA_NOVEDADES}`;
  const gestionar = `${app}/ajustes?tab=cuenta`;
  const e = emailEscape;
  const saludo = saludoNovedades(nombre);

  const html = `<!doctype html>
<html lang="es" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<meta name="x-apple-disable-message-reformatting">
<title>Alexia tiene nueva cara</title>
<style>
  body{margin:0;padding:0;background:#FBF7F1}
  a{color:#14776A}
  @media (max-width:620px){
    .wrap{width:100% !important}
    .pad{padding-left:22px !important;padding-right:22px !important}
    .h1{font-size:26px !important}
    .store{display:block !important;width:100% !important;margin:0 0 10px 0 !important}
  }
</style>
</head>
<body style="margin:0;padding:0;background:#FBF7F1">
<!-- preheader (no se ve; sale en la bandeja de entrada) -->
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#FBF7F1">${PREHEADER_NOVEDADES}&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;</div>

<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#FBF7F1">
<tr><td align="center" style="padding:28px 12px">

  <table role="presentation" class="wrap" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background:#FFFFFF;border-radius:16px;overflow:hidden">

    <!-- logo -->
    <tr><td class="pad" style="padding:30px 40px 6px 40px">
      <img src="${e(img)}/logo/logotipo-pdf.png" width="150" alt="alexia rest." style="display:block;width:150px;height:auto;border:0">
    </td></tr>

    <!-- gancho -->
    <tr><td class="pad" style="padding:18px 40px 0 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif;color:#1B1A17">
      <h1 class="h1" style="margin:0 0 16px 0;font-size:30px;line-height:1.15;font-weight:700;letter-spacing:-0.5px;color:#1B1A17">Hoy vas a ver Alexia distinta. Tus datos, no.</h1>
      <p style="margin:0 0 14px 0;font-size:16px;line-height:1.6;color:#37352F">${e(saludo)}</p>
      <p style="margin:0 0 14px 0;font-size:16px;line-height:1.6;color:#37352F">Cuando abras Alexia hoy, la vas a encontrar cambiada. No pasa nada: tus recetas, tus ventas y tus gastos están exactamente donde los dejaste. Lo que cambió es cómo se ve y cómo se llaman algunas cosas.</p>
      <p style="margin:0 0 20px 0;font-size:16px;line-height:1.6;color:#37352F">Te lo enseñamos en un minuto:</p>
    </td></tr>

    <!-- vídeo -->
    <tr><td class="pad" style="padding:0 40px">
      <a href="${e(video)}" style="display:block;text-decoration:none">
        <img src="${e(img)}${NOVEDADES_MINIATURA}" width="520" alt="Ver el vídeo: Alexia tiene nueva cara (1:11)" style="display:block;width:100%;max-width:520px;height:auto;border:0;border-radius:12px">
      </a>
    </td></tr>

    <!-- dónde está ahora -->
    <tr><td class="pad" style="padding:30px 40px 0 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif">
      <p style="margin:0 0 10px 0;font-size:13px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;color:#6F6E69">Dónde está ahora lo que ya usabas</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-top:1px solid #EDECE9">
        <tr><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:14px;color:#91908C;width:44%">Ingresos</td><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:15px;font-weight:600;color:#0E4A42"><span style="color:#91908C;font-weight:500">Finanzas ›</span> Ventas</td></tr>
        <tr><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:14px;color:#91908C">Cuenta de resultados</td><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:15px;font-weight:600;color:#0E4A42"><span style="color:#91908C;font-weight:500">Finanzas ›</span> Estado de resultados</td></tr>
        <tr><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:14px;color:#91908C">Recetas y costeo</td><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:15px;font-weight:600;color:#0E4A42"><span style="color:#91908C;font-weight:500">Costeo ›</span> Tu carta y Preparaciones</td></tr>
        <tr><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:14px;color:#91908C">Inventario</td><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:15px;font-weight:600;color:#0E4A42"><span style="color:#91908C;font-weight:500">Costeo ›</span> Recuento mensual</td></tr>
        <tr><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:14px;color:#91908C">Ajustes</td><td style="padding:11px 0;border-bottom:1px solid #F3F2EF;font-size:15px;font-weight:600;color:#0E4A42">Configuración</td></tr>
      </table>
    </td></tr>

    <!-- lo nuevo -->
    <tr><td class="pad" style="padding:26px 40px 0 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif">
      <p style="margin:0 0 10px 0;font-size:13px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;color:#6F6E69">Lo nuevo</p>
      <p style="margin:0 0 8px 0;font-size:15px;line-height:1.55;color:#37352F"><b style="color:#1B1A17">Para hoy:</b> lo que tienes pendiente, como una lista que vas tachando.</p>
      <p style="margin:0 0 8px 0;font-size:15px;line-height:1.55;color:#37352F"><b style="color:#1B1A17">Un «?» junto a cada número:</b> tócalo y te explica qué es.</p>
      <p style="margin:0 0 20px 0;font-size:15px;line-height:1.55;color:#37352F"><b style="color:#1B1A17">Alexia en tu teléfono:</b> ya disponible en App Store y Google Play.</p>
    </td></tr>

    <!-- botón principal -->
    <tr><td class="pad" align="left" style="padding:0 40px">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
        <td style="border-radius:999px;background:#14776A"><a href="${e(app)}" style="display:inline-block;padding:14px 28px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif;font-size:16px;font-weight:700;color:#FFFFFF;text-decoration:none;border-radius:999px">Entrar a Alexia</a></td>
      </tr></table>
    </td></tr>

    <!-- tiendas -->
    <tr><td class="pad" style="padding:26px 40px 0 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif">
      <p style="margin:0 0 10px 0;font-size:14px;color:#6F6E69">Baja la app:</p>
      <a class="store" href="${e(APP_STORE_URL)}" style="display:inline-block;margin:0 8px 0 0;padding:10px 18px;border-radius:10px;background:#1B1A17;color:#FFFFFF;text-decoration:none;font-size:15px;font-weight:700">App Store</a><a class="store" href="${e(GOOGLE_PLAY_URL)}" style="display:inline-block;padding:10px 18px;border-radius:10px;background:#1B1A17;color:#FFFFFF;text-decoration:none;font-size:15px;font-weight:700">Google Play</a>
    </td></tr>

    <!-- cierre -->
    <tr><td class="pad" style="padding:28px 40px 34px 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif">
      <p style="margin:0 0 14px 0;font-size:15px;line-height:1.6;color:#37352F">Si algo no lo encuentras, responde a este correo o <a href="${e(enlaces.whatsappUrl)}" style="color:#14776A;font-weight:600">escríbenos por WhatsApp</a>. Te contesta una persona.</p>
      <p style="margin:0;font-size:15px;line-height:1.6;color:#37352F">El equipo de Alexia</p>
    </td></tr>
  </table>

  <!-- pie -->
  <table role="presentation" class="wrap" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px">
    <tr><td class="pad" align="center" style="padding:18px 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif;font-size:12px;line-height:1.6;color:#91908C">
      Recibes este correo porque tienes una cuenta en Alexia Rest.<br>
      Alexia Rest · Polar Consulting Group 33 SL · <a href="${e(gestionar)}" style="color:#91908C">Gestionar correos</a>
    </td></tr>
  </table>

</td></tr>
</table>
</body>
</html>
`;

  const texto = [
    'Hoy vas a ver Alexia distinta. Tus datos, no.',
    saludo,
    'Cuando abras Alexia hoy, la vas a encontrar cambiada. No pasa nada: tus recetas, tus ventas y tus gastos están exactamente donde los dejaste. Lo que cambió es cómo se ve y cómo se llaman algunas cosas.',
    `Te lo enseñamos en un minuto: ${video}`,
    [
      'DÓNDE ESTÁ AHORA LO QUE YA USABAS',
      '- Ingresos → Finanzas › Ventas',
      '- Cuenta de resultados → Finanzas › Estado de resultados',
      '- Recetas y costeo → Costeo › Tu carta y Preparaciones',
      '- Inventario → Costeo › Recuento mensual',
      '- Ajustes → Configuración',
    ].join('\n'),
    [
      'LO NUEVO',
      '- Para hoy: lo que tienes pendiente, como una lista que vas tachando.',
      '- Un «?» junto a cada número: tócalo y te explica qué es.',
      '- Alexia en tu teléfono: ya disponible en App Store y Google Play.',
    ].join('\n'),
    `Entrar a Alexia: ${app}`,
    `Baja la app:\nApp Store: ${APP_STORE_URL}\nGoogle Play: ${GOOGLE_PLAY_URL}`,
    `Si algo no lo encuentras, responde a este correo o escríbenos por WhatsApp: ${enlaces.whatsappUrl}. Te contesta una persona.`,
    'El equipo de Alexia',
    '--',
    `Recibes este correo porque tienes una cuenta en Alexia Rest.\nAlexia Rest · Polar Consulting Group 33 SL · Gestionar correos: ${gestionar}`,
  ].join('\n\n');

  return { asunto: ASUNTO_NOVEDADES, preheader: PREHEADER_NOVEDADES, html, texto };
}
